import { SpecialAnchorTypes, ToolBaseTypes, ToolTypes } from '@labradorsports/constants';

import { FieldPiece } from '../../models/index.js';
import { PluginContext } from '../../plugins/api.js';
import {
    generateShadingAnchors,
    generateShadingSelectedAnchors,
    handleShadingDrag,
    handleShadingDragEnd,
} from '../../utils/index.js';
import { getFootballToolFrameIdx } from '../move-placement-frame.js';

function getFootballShadingFrameIdx(anchor: { frameIdx?: number }, piece: FieldPiece | undefined): number {
    if (typeof anchor.frameIdx === 'number') {
        return anchor.frameIdx;
    }

    if (typeof piece?.props?.frameIdx === 'number') {
        return piece.props.frameIdx;
    }

    return getFootballToolFrameIdx(anchor);
}

function withShadingFrameIdx<T extends { frameIdx?: number }>(anchors: T[], frameIdx: number): T[] {
    return anchors.map((anchor) => ({
        ...anchor,
        frameIdx,
    }));
}

export default {
    baseType: ToolBaseTypes.PIECE,
    types: [ToolTypes.ELLIPSE, ToolTypes.RECTANGLE],
    specialAnchorTypes: [SpecialAnchorTypes.SHADING_RESIZE, SpecialAnchorTypes.SHADING_ROTATE],
    generateAnchors: ({ play, frame, fieldViewport }) => {
        if (play.fieldSetup) {
            return generateShadingAnchors({ play, frame, fieldViewport });
        }

        return play.frameSnapshots.flatMap((frameSnapshot, frameIdx) =>
            withShadingFrameIdx(generateShadingAnchors({ play, frame: frameSnapshot, fieldViewport }), frameIdx)
        );
    },

    generateSelectedAnchors: (context, selected) => {
        const frameIdx =
            typeof selected.props?.frameIdx === 'number' ? selected.props.frameIdx : context.frame.idx;

        return withShadingFrameIdx(generateShadingSelectedAnchors(context, selected), frameIdx);
    },

    handleDragEnd: (context, anchor, offset, clamped) => {
        const { api, play } = context;
        const piece = play.field.getFieldPiece(anchor.id);
        const frameIdx = getFootballShadingFrameIdx(anchor, piece);
        const frame = play.frameSnapshots[frameIdx];

        const wrappedApi = {
            ...api,
            updateFieldPiece: (update: any) => {
                api.updateFieldPiece({ ...update, frameIdx });
            },
        };

        handleShadingDragEnd({ ...context, api: wrappedApi, frame }, anchor, offset, clamped);
    },

    handleDrag: (context, anchor, offset, clamped) => {
        const { play, frames } = context;
        const piece = play.field.getFieldPiece(anchor.id);
        const frameIdx = getFootballShadingFrameIdx(anchor, piece);
        const frame = frames?.[frameIdx];

        if (!frame) {
            return;
        }

        handleShadingDrag({ ...context, frame }, anchor, offset, clamped);
    },
} as PlayPlugin<PluginContext>;
